import { Button, Popconfirm } from 'antd'
import { observer } from 'mobx-react-lite'
import { useRef, useState, type DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { MdOutlineFileUpload, MdPhotoCamera } from 'react-icons/md'

import { userManager } from '@/Core/Managers'
import { UserAvatarContent } from '@/Ship/Components/UI/UserPhoto/UserAvatarContent'
import photoStyles from '@/Ship/Components/UI/UserPhoto/UserPhoto.module.css'
import { useUserPhotoEditor } from '@/Ship/Components/UI/UserPhoto/useUserPhotoEditor'
import { cx } from '@/Ship/Helpers/cssClass'
import { PHOTO_MAX_SOURCE_MB } from '@/Ship/Helpers/userPhoto'
import { resolveInitials } from '@/Ship/Templates/Layouts/Components/UserProfileBlock/UserProfileBlock'

import shell from '@/Ship/Components/UI/SettingsShell/SettingsShell.module.css'

import styles from '../UserProfile.module.css'

const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes('Files')

/**
 * Розділ «Фото профілю»: великий аватар (клік або перетягування файлу — змінити),
 * кнопки «Завантажити/Змінити фото» і «Видалити фото» з підтвердженням.
 * Вибір, перевірка, обрізання і збереження — спільний Ship useUserPhotoEditor.
 */
export const ProfilePhoto = observer(() => {
    const { t } = useTranslation()
    const tp = (key: string, options?: Record<string, unknown>) => t(key, { ns: 'Setting.Profile', ...options })
    const editor = useUserPhotoEditor()
    const [dragOver, setDragOver] = useState(false)
    // dragenter/dragleave спрацьовують і на дочірніх елементах — рахуємо глибину
    const dragDepth = useRef(0)

    const user = (userManager.user ?? {}) as Record<string, any>
    const changeLabel = editor.hasPhoto ? tp('Change photo') : tp('Upload photo')

    const onDragEnter = (event: DragEvent<HTMLButtonElement>) => {
        if (!hasFiles(event)) return
        event.preventDefault()
        dragDepth.current += 1
        setDragOver(true)
    }

    const onDragLeave = () => {
        dragDepth.current = Math.max(0, dragDepth.current - 1)
        if (dragDepth.current === 0) setDragOver(false)
    }

    const onDrop = (event: DragEvent<HTMLButtonElement>) => {
        event.preventDefault()
        dragDepth.current = 0
        setDragOver(false)
        void editor.accept(event.dataTransfer.files?.[0])
    }

    return (
        <div className={cx(shell, 'section')}>
            <div className={cx(shell, 'sectionHead')}>
                <h2 className={cx(shell, 'sectionTitle')}>{tp('Profile photo')}</h2>
            </div>
            <div className={cx(styles, 'photoRow')}>
                <button
                    type="button"
                    className={`${photoStyles['changer']}${dragOver ? ` ${photoStyles['changerActive']}` : ''}${editor.busy ? ` ${photoStyles['changerBusy']}` : ''}`}
                    aria-label={changeLabel}
                    title={changeLabel}
                    disabled={editor.busy}
                    onClick={editor.pick}
                    onDragEnter={onDragEnter}
                    onDragOver={(event) => hasFiles(event) && event.preventDefault()}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    data-testid="profile-photo-dropzone"
                >
                    <span className={cx(styles, 'photo')} aria-hidden="true">
                        <UserAvatarContent photo={user['photo']} initials={resolveInitials(user['fullName'] || '')} />
                    </span>
                    <span className={photoStyles['changerOverlay']} aria-hidden="true">
                        <MdPhotoCamera />
                        {dragOver ? tp('Drop here') : tp('Change')}
                    </span>
                </button>
                <div className={cx(styles, 'photoMeta')}>
                    <div className={cx(styles, 'photoActions')}>
                        <Button icon={<MdOutlineFileUpload />} loading={editor.busy} onClick={editor.pick}>
                            {changeLabel}
                        </Button>
                        {editor.hasPhoto && (
                            <Popconfirm
                                title={tp('Delete photo?')}
                                okText={tp('Delete')}
                                okButtonProps={{ danger: true }}
                                cancelText={tp('Cancel')}
                                onConfirm={() => void editor.remove()}
                            >
                                <Button type="text" danger disabled={editor.busy}>
                                    {tp('Delete photo')}
                                </Button>
                            </Popconfirm>
                        )}
                    </div>
                    <p className={cx(styles, 'photoHint')}>{tp('Photo hint', { max: PHOTO_MAX_SOURCE_MB })}</p>
                </div>
            </div>
            {editor.holder}
        </div>
    )
})
